<template>
  <main class="game-prize-page">
    <div v-if="initialLoading" class="initial-loading" role="status" aria-live="polite">
      <van-loading color="#fff" size="28px">活动加载中...</van-loading>
    </div>

    <div v-else-if="!activityReady" class="activity-empty">
      <p>{{ pageMessage || "暂无进行中的专题活动" }}</p>
    </div>

    <template v-else>
      <img class="hero" :src="heroImage" :alt="`${activityTitle}主图`" />

      <p v-if="pageMessage" class="page-message">{{ pageMessage }}</p>

      <section class="game-card">
        <img class="game-icon" :src="gameInfo.icon" :alt="gameInfo.gamename" />
        <div class="game-meta">
          <h1>{{ gameInfo.gamename }}</h1>
          <span class="server-tag">
            {{ gameInfo.server_tag || "动态开服" }}
          </span>
          <span v-if="gameInfo.discount_tag" class="discount-tag">{{ gameInfo.discount_tag }}</span>
        </div>
        <button class="game-download" type="button" @click="goGamedetails">下载</button>
      </section>

      <section class="lottery-panel">
        <SectionTitle text="抽奖必得好礼" />

        <div class="lottery-grid">
          <button
            v-for="cell in gridCells"
            :key="cell.key"
            class="prize-cell"
            :class="{ active: cell.pathIndex === activeStep }"
            :style="{ gridArea: cell.area }"
            type="button"
            disabled
          >
            <img :src="cell.image" :alt="cell.label" />
            <span v-if="cell.label" class="prize-title">{{ cell.label }}</span>
          </button>

          <button
            class="draw-button"
            :class="{ drawing: isDrawing }"
            type="button"
            :disabled="drawRequestPending || isDrawing || loading"
            aria-label="开始抽奖"
            @click="startDraw"
          >
            <strong>{{ isDrawing ? "抽奖中" : "抽奖" }}</strong>
            <span>抽奖次数:{{ drawCount }}</span>
          </button>
        </div>

        <div class="winner-ticker" aria-live="polite">
          <transition name="ticker-avatar" mode="out-in">
            <img
              :key="tickerIndex"
              :src="currentTicker.avatar"
              :alt="currentTicker.name"
            />
          </transition>
          <div class="ticker-window">
            <transition name="ticker" mode="out-in">
              <p :key="tickerIndex">
                恭喜 <b>{{ currentTicker.name }}</b> 获得
                <em>{{ currentTicker.prize }}</em>
              </p>
            </transition>
          </div>
          <transition name="ticker-time" mode="out-in">
            <time :key="tickerIndex">{{ currentTicker.time }}</time>
          </transition>
        </div>

        <div class="panel-actions">
          <button type="button" @click="handleRules">活动说明</button>
          <button type="button" @click="handlePrizes">查看奖品</button>
        </div>
      </section>

      <section class="tasks-card">
        <SectionTitle text="当前任务" />
        <p class="section-tip">每完成一个任务，抽奖机会 +1</p>
        <ul>
          <li v-for="task in tasks" :key="task.id || task.title">
            <span class="task-marker"></span>
            <div>
              <h3>{{ task.title }}</h3>
              <p>完成奖励抽奖次数 +{{ task.reward || 1 }}，仅一次</p>
            </div>
            <button class="task-status" :class="{ done: task.done }" type="button" :disabled="task.done" @click="handleTask(task)">
              {{ task.done ? "已完成" : (task.action === "browse" ? "去完成" : "未完成") }}
            </button>
          </li>
        </ul>
      </section>

      <section class="features-card">
        <SectionTitle text="游戏特色" />
        <div class="feature-images">
          <img v-for="(image, index) in featureImages" :key="image" :src="image" :alt="`游戏特色图 ${index + 1}`" />
        </div>
        <p>{{ gameInfo.description }}</p>
      </section>

      <button class="bottom-download" type="button" @click="goGamedetails">下载</button>
    </template>
  </main>

  <van-popup
    v-model:show="ruleShow"
    class="game-prize-popup"
    overlay-class="game-prize-overlay"
  >
    <div class="rule-popup">
      <div
        class="rule-content"
        :style="{ backgroundImage: `url(${popupAssets.ruleBackground})` }"
      >
        <div class="rule-info">
          <pre>{{ activityRule }}</pre>
        </div>
      </div>
      <button type="button" @click="closeRule">关闭</button>
      <span>
        <van-checkbox
          v-model="noTips"
          shape="square"
          checked-color="#6f4edd"
          icon-size="15px"
          @change="tipsChange"
        >
          下次不再提示
        </van-checkbox>
      </span>
    </div>
  </van-popup>

  <van-popup
    v-model:show="awardShow"
    class="game-prize-popup"
    overlay-class="game-prize-overlay"
    closeable
  >
    <div
      class="award-content"
      :style="{ backgroundImage: `url(${popupAssets.awardBackground})` }"
    >
      <div class="award-list">
        <ul>
          <li v-for="item in awardRecords" :key="item.id">
            <p>{{ item.lottery_log_cont }}</p>
            <p>{{ dateYMDHM(item.lottery_log_time) }}</p>
          </li>
        </ul>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import { computed, defineComponent, h, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { dateYMDHM } from "@/utils/date";
import { detect } from "@/utils/device";
import { getQueryString } from "@/utils/location";
import { getToken, setToken } from "@/utils/auth";
import { Toast } from "vant";
import { completeGamePrizeTask, drawGamePrize, getGamePrizeInfo } from "@/api/activity";

const assets = {
  hero: require("../../assets/webActivity/gamePrize/hero.png"),
  gameIcon: require("../../assets/webActivity/gamePrize/game-icon.png"),
  avatar: require("../../assets/webActivity/gamePrize/avatar.png"),
};
const prizeTypeImages = Object.freeze({
  1: require("../../assets/webActivity/gamePrize/prize-type-1.png"),
  2: require("../../assets/webActivity/gamePrize/prize-type-2.png"),
  3: require("../../assets/webActivity/gamePrize/prize-type-3.png"),
  4: require("../../assets/webActivity/gamePrize/prize-type-4.png"),
  5: require("../../assets/webActivity/gamePrize/prize-type-5.png"),
  6: require("../../assets/webActivity/gamePrize/prize-type-6.png"),
});
const fallbackPrizes = [
  { type: 2, title: "积分" },
  { type: 5, title: "抵扣券" },
  { type: 6, title: "余额" },
  { type: 3, title: "游戏礼包" },
  { type: 2, title: "积分礼包" },
  { type: 3, title: "游戏礼包" },
  { type: 3, title: "游戏礼包" },
  { type: 2, title: "积分礼包" },
].map((item, index) => ({ ...item, id: index + 1, image: prizeTypeImages[item.type] }));
const fallbackFeatures = [
  require("../../assets/webActivity/gamePrize/feature-1.png"),
  require("../../assets/webActivity/gamePrize/feature-2.png"),
  require("../../assets/webActivity/gamePrize/feature-3.png"),
];
const defaultRule = `1. 活动规则
活动期间完成页面任务可获得相应抽奖机会，点击九宫格中间的“抽奖”按钮参与抽奖。

2. 奖励说明
积分、优惠券和赠送余额发放至个人中心对应账户；游戏礼包发放至礼包记录，具体到账结果以页面提示为准。

3. 其他说明
同一实名、手机号或手机设备符合任一条件均视为同一用户；如有疑问，请在 APP 内通过“我的-联系客服”咨询。`;
const state = reactive({ gid: "3341", token: "" });
const gameInfo = reactive({ gid: "3341", gamename: "精灵物语", icon: assets.gameIcon, server_tag: "", discount_tag: "0.1折", description: "游戏专题福利活动，完成任务即可参与抽奖。" });
const activityTitle = ref("限时福利");
const activityRule = ref(defaultRule);
const heroImage = ref(assets.hero);
const prizes = ref(fallbackPrizes);
const tasks = ref([
  { id: 1, title: "登录游戏（活动期间）", reward: 1, done: 0, action: "game" },
  { id: 2, title: "任意消费（活动期间）", reward: 1, done: 0, action: "game" },
  { id: 3, title: "浏览游戏专区（活动期间）", reward: 1, done: 0, action: "browse" },
  { id: 4, title: "游戏等级达到10级", reward: 1, done: 0, action: "game" },
]);
const featureImages = ref(fallbackFeatures);
const tickers = ref([{ avatar: assets.avatar, name: "幸运玩家", prize: "专题好礼", time: "刚刚" }]);
const awardRecords = ref([]);
const drawCount = ref(0);
const activeStep = ref(-1);
const isDrawing = ref(false);
const drawRequestPending = ref(false);
const loading = ref(false);
const initialLoading = ref(true);
const activityReady = ref(false);
const pageMessage = ref("");
const tickerIndex = ref(0);
const ruleShow = ref(false);
const awardShow = ref(false);
const noTips = ref(false);
const gridAreas = ["one", "two", "three", "six", "nine", "eight", "seven", "four"];
const popupAssets = {
  ruleBackground: "https://oss.zhekoushenqi.com/data/homegameActivity/icon_rule_bg.png",
  awardBackground: "https://oss.zhekoushenqi.com/data/homegameActivity/icon_prize_bg.png",
};

const SectionTitle = defineComponent({
  name: "SectionTitle",
  props: { text: { type: String, required: true } },
  setup(props) {
    return () => h("div", { class: "section-title" }, [h("span", { "aria-hidden": "true" }, "✦"), h("h2", props.text), h("span", { "aria-hidden": "true" }, "✦")]);
  },
});
const resolvePrizeImage = (prize, index) => {
  const customImage = typeof prize.image === "string" ? prize.image.trim() : prize.image;
  return customImage
    || prizeTypeImages[Number(prize.type)]
    || fallbackPrizes[index % fallbackPrizes.length].image;
};
const gridCells = computed(() => prizes.value.slice(0, 8).map((prize, pathIndex) => ({
  ...prize,
  key: prize.id || pathIndex,
  label: prize.title || "",
  pathIndex,
  area: gridAreas[pathIndex],
  image: resolvePrizeImage(prize, pathIndex),
})));
const currentTicker = computed(() => tickers.value[tickerIndex.value % tickers.value.length]);

const relativeTime = timestamp => {
  const seconds = Math.max(0, Math.floor(Date.now() / 1000) - Number(timestamp || 0));
  if (seconds < 60) return "刚刚";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}分钟前`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}小时前`;
  return `${Math.floor(seconds / 86400)}天前`;
};

const formatActivityDate = timestamp => {
  const value = Number(timestamp);
  if (!Number.isFinite(value) || value <= 0) return "";
  const date = new Date(value < 1e12 ? value * 1000 : value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
};

const buildActivityRule = activity => {
  const remoteRule = typeof activity.rule === "string" ? activity.rule.trim() : "";
  const rule = remoteRule || defaultRule;
  const startTime = formatActivityDate(activity.start_time);
  const endTime = formatActivityDate(activity.end_time);
  if (!startTime || !endTime) return rule;
  return `活动时间：\n${startTime} - ${endTime}\n\n${rule}`;
};

const loadActivity = async (showLoading = true) => {
  state.gid = String(getQueryString("gid") || state.gid || "3341");
  const queryToken = getQueryString("token");
  if (queryToken && queryToken !== "undefined") setToken(queryToken);
  state.token = queryToken && queryToken !== "undefined" ? queryToken : (getToken() || "");
  if (showLoading) loading.value = true;
  pageMessage.value = "";
  try {
    const response = await getGamePrizeInfo({ gid: state.gid, token: state.token });
    const result = response.data || {};
    if (Number(result.code) !== 1 || !result.data) throw new Error(result.msg || "活动加载失败");
    const data = result.data;
    const activity = data.activity || {};
    activityTitle.value = activity.title || "限时福利";
    activityRule.value = buildActivityRule(activity);
    heroImage.value = (data.game && data.game.hero_image) || assets.hero;
    Object.assign(gameInfo, data.game || {});
    gameInfo.icon = gameInfo.icon || assets.gameIcon;
    prizes.value = (data.prizes || []).map((item, index) => ({
      ...item,
      image: resolvePrizeImage(item, index),
    }));
    if (!prizes.value.length) {
      prizes.value = fallbackPrizes;
      pageMessage.value = "该活动尚未配置专题抽奖奖品，当前展示预览奖品";
    }
    tasks.value = data.tasks || [];
    featureImages.value = data.game.feature_images && data.game.feature_images.length ? data.game.feature_images.slice(0, 5) : fallbackFeatures;
    awardRecords.value = data.my_records || [];
    drawCount.value = Number(data.draw_count || 0);
    tickers.value = (data.winner_records || []).map(item => ({
      avatar: item.avatar && /^https?:/.test(item.avatar) ? item.avatar : assets.avatar,
      name: item.user_nicename || item.username || "幸运玩家", prize: item.lottery_log_cont, time: relativeTime(item.lottery_log_time),
    }));
    if (!tickers.value.length) tickers.value = [{ avatar: assets.avatar, name: "幸运玩家", prize: "专题好礼", time: "刚刚" }];
    tickerIndex.value = 0;
    activityReady.value = true;
  } catch (error) {
    pageMessage.value = error && error.message === "Network Error"
      ? "活动加载失败，请检查网络后重试"
      : (error.message || "活动加载失败，请稍后重试");
    if (showLoading) activityReady.value = false;
  } finally {
    loading.value = false;
    if (showLoading) initialLoading.value = false;
  }
};

const animateDraw = target => new Promise(resolve => {
  activeStep.value = 0;
  const totalSteps = prizes.value.length * 3 + Number(target);
  let step = 0;
  const run = () => {
    activeStep.value = step % prizes.value.length;
    if (step >= totalSteps) { resolve(); return; }
    step += 1;
    drawTimer = window.setTimeout(run, step > totalSteps - 7 ? 150 + (step - (totalSteps - 7)) * 35 : 75);
  };
  run();
});

const startDraw = async () => {
  if (drawRequestPending.value || isDrawing.value) return;
  if (!state.token) { Toast("请登录后参与抽奖"); return; }
  if (drawCount.value <= 0) { Toast("抽奖次数不足"); return; }
  drawRequestPending.value = true;
  isDrawing.value = true;
  try {
    const response = await drawGamePrize({ gid: state.gid, token: state.token });
    const result = response.data || {};
    if (Number(result.code) !== 1) throw new Error(result.msg || "抽奖失败");
    await animateDraw(result.data.pnum);
    drawCount.value = Number(result.data.draw_count || 0);
    Toast(result.msg);
    await loadActivity(false);
  } catch (error) {
    Toast(error.message || "抽奖失败，请稍后重试");
  } finally {
    isDrawing.value = false;
    drawRequestPending.value = false;
  }
};

const handleTask = async task => {
  if (task.done) return;
  if (task.action !== "browse") { Toast("请先进入游戏完成该任务"); goGamedetails(); return; }
  if (!state.token) { Toast("请登录后完成任务"); return; }
  try {
    const response = await completeGamePrizeTask({ gid: state.gid, token: state.token, task_id: task.id });
    const result = response.data || {};
    if (Number(result.code) !== 1) throw new Error(result.msg || "任务领取失败");
    Toast(result.msg);
    await loadActivity(false);
    goGamedetails();
  } catch (error) { Toast(error.message || "任务领取失败"); }
};
const handleRules = () => { ruleShow.value = true; };
const closeRule = () => { ruleShow.value = false; };
const tipsChange = () => localStorage.setItem(`gamePrizeRules_${state.gid}`, noTips.value ? "1" : "0");
const handlePrizes = () => { awardShow.value = true; };
const goGamedetails = () => {
  if (!state.gid) return;
  if (detect() === "android" || detect() === "harmony") window.document.location = "gogame://" + state.gid;
  else window.parent.postMessage({ eventType: "openEvent", gid: state.gid }, "*");
};
let tickerTimer;
let drawTimer;
onMounted(async () => {
  await loadActivity();
  noTips.value = localStorage.getItem(`gamePrizeRules_${state.gid}`) === "1";
  if (activityReady.value && !noTips.value) ruleShow.value = true;
  tickerTimer = window.setInterval(() => { tickerIndex.value = (tickerIndex.value + 1) % tickers.value.length; }, 2600);
});
onBeforeUnmount(() => { window.clearInterval(tickerTimer); window.clearTimeout(drawTimer); });
</script>

<style scoped>
@font-face {
  font-family: "YouSheBiaoTiHei";
  src: url("../../styles/font/ysbth_simplify.ttf") format("truetype");
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}

.game-prize-page {
  --u: calc(100vw / 390);
  width: 100vw;
  min-height: 100vh;
  margin: 0;
  padding-bottom: calc(96 * var(--u));
  overflow: hidden;
  color: #315373;
  background: #2c8cff;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

:global(html), :global(body), :global(#app) { overflow-x: hidden; }

button { font: inherit; }

.initial-loading,
.activity-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: calc(24 * var(--u));
  box-sizing: border-box;
  color: #fff;
  text-align: center;
}

.initial-loading :deep(.van-loading__text) {
  color: #fff;
  font-size: calc(14 * var(--u));
}

.activity-empty p {
  width: 100%;
  padding: calc(14 * var(--u)) calc(18 * var(--u));
  border-radius: calc(12 * var(--u));
  background: rgba(20, 76, 190, .72);
  font-size: calc(14 * var(--u));
  line-height: 1.5;
}

.page-message {
  width: calc(358 * var(--u));
  margin: calc(10 * var(--u)) auto;
  padding: calc(9 * var(--u)) calc(12 * var(--u));
  border-radius: calc(10 * var(--u));
  color: #fff;
  background: rgba(20, 76, 190, .72);
  font-size: calc(12 * var(--u));
  line-height: 1.5;
  text-align: center;
}

.hero {
  display: block;
  width: 100%;
  height: calc(219 * var(--u));
  object-fit: cover;
}

.game-card {
  position: relative;
  display: flex;
  align-items: center;
  width: calc(358 * var(--u));
  height: calc(116 * var(--u));
  margin: calc(16 * var(--u)) auto;
  padding: calc(14 * var(--u)) calc(12 * var(--u));
  overflow: hidden;
  border-radius: calc(20 * var(--u));
  background: #fff;
}

.game-icon {
  width: calc(92 * var(--u));
  height: calc(92 * var(--u));
  border-radius: calc(18 * var(--u));
  object-fit: cover;
}

.game-meta {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  gap: calc(6 * var(--u));
  margin-left: calc(12 * var(--u));
}

.game-meta h1 { max-width: calc(130 * var(--u)); color: #333; font-size: calc(18 * var(--u)); line-height: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.server-tag {
  display: inline-flex;
  align-items: center;
  width: auto;
  max-width: 100%;
  height: calc(22 * var(--u));
  padding: 0 calc(4 * var(--u));
  gap: calc(2 * var(--u));
  border-radius: calc(5 * var(--u));
  color: #12cec7;
  background: rgba(18, 206, 199, .1);
  font-family: "YouSheBiaoTiHei", "PingFang SC", sans-serif;
  box-sizing: border-box;
  font-size: calc(11 * var(--u));
  line-height: calc(11 * var(--u));
  font-weight: 400;
  white-space: nowrap;
}
.server-tag i {
  position: relative;
  flex: 0 0 calc(12 * var(--u));
  width: calc(12 * var(--u));
  height: calc(12 * var(--u));
}
.server-tag i::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(4 * var(--u));
  height: calc(4 * var(--u));
  border-top: calc(1.5 * var(--u)) solid currentColor;
  border-right: calc(1.5 * var(--u)) solid currentColor;
  content: "";
  transform: translate(-65%, -50%) rotate(45deg);
}
.discount-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-width: calc(41 * var(--u));
  height: calc(18 * var(--u));
  padding: 0 calc(4 * var(--u));
  box-sizing: border-box;
  border-radius: calc(3 * var(--u));
  color: #fff;
  background: linear-gradient(92deg, #ff6666, #ffac5f);
  font-family: "YouSheBiaoTiHei", "PingFang SC", sans-serif;
  font-size: calc(14 * var(--u));
  line-height: normal;
  font-weight: 400;
  white-space: nowrap;
}
.game-download {
  position: absolute;
  top: 50%;
  right: calc(16 * var(--u));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: calc(36 * var(--u));
  padding: 0 calc(16 * var(--u));
  transform: translateY(-50%);
  border: 0;
  border-radius: 99Px;
  color: #fff;
  background: #00d9c3;
  font-size: calc(16 * var(--u));
  line-height: 1;
}

.lottery-panel {
  position: relative;
  width: calc(358 * var(--u));
  height: calc(538 * var(--u));
  margin: 0 auto calc(16 * var(--u));
  padding-top: calc(70 * var(--u));
  border: calc(1 * var(--u)) solid #80d9ff;
  border-radius: calc(20 * var(--u));
  background: #3a7cff;
  box-shadow: inset 0 calc(6 * var(--u)) calc(10 * var(--u)) rgba(255,255,255,.25);
}

.section-title {
  position: absolute;
  z-index: 2;
  top: 0;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(8 * var(--u));
  width: calc(210 * var(--u));
  height: calc(50 * var(--u));
  transform: translateX(-50%);
  border-radius: 0 0 calc(30 * var(--u)) calc(30 * var(--u));
  color: #fff;
  background: #149cff;
  box-shadow: 0 calc(-1 * var(--u)) calc(4 * var(--u)) rgba(0,0,0,.25);
}
.section-title h2 { font-size: calc(21 * var(--u)); white-space: nowrap; }
.section-title span { color: #fff25c; font-size: calc(30 * var(--u)); text-shadow: 0 0 calc(8 * var(--u)) #fff; }

.lottery-grid {
  display: grid;
  grid-template-areas: "one two three" "four draw six" "seven eight nine";
  grid-template-columns: repeat(3, calc(100 * var(--u)));
  grid-template-rows: repeat(3, calc(100 * var(--u)));
  gap: calc(13 * var(--u));
  justify-content: center;
}

.prize-cell, .draw-button {
  position: relative;
  width: calc(100 * var(--u));
  height: calc(100 * var(--u));
  overflow: visible;
  border: 0;
  border-radius: calc(16 * var(--u));
}
.prize-cell {
  padding: 0;
  appearance: none;
  line-height: 0;
  border-radius: calc(16 * var(--u));
  background: transparent;
  opacity: 1;
}
.prize-cell img { display: block; width: 100%; height: 100%; border-radius: calc(14 * var(--u)); object-fit: contain; }
.prize-title {
  position: absolute;
  z-index: 1;
  right: calc(4 * var(--u));
  bottom: calc(8 * var(--u));
  left: calc(4 * var(--u));
  overflow: hidden;
  color: #ff5573;
  font-size: calc(13 * var(--u));
  font-weight: 700;
  line-height: calc(16 * var(--u));
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-shadow: 0 calc(1 * var(--u)) calc(1 * var(--u)) rgba(203, 54, 86, .18);
}
.prize-cell.active { border-radius: calc(16 * var(--u)); box-shadow: 0 0 0 calc(3 * var(--u)) #fff, 0 0 calc(14 * var(--u)) #fff25c; transform: scale(1.035); }

.draw-button { grid-area: draw; display: flex; flex-direction: column; align-items: center; justify-content: center; border: 0; color: #fff; background: #5ae4f4; }
.draw-button strong { font-size: calc(26 * var(--u)); text-shadow: calc(1 * var(--u)) calc(1 * var(--u)) 0 #527dff; }
.draw-button span { margin-top: calc(10 * var(--u)); font-size: calc(12 * var(--u)); }
.draw-button.drawing { filter: brightness(.95); }

.winner-ticker { position: absolute; left: calc(17 * var(--u)); top: calc(403 * var(--u)); display: flex; align-items: center; width: calc(322 * var(--u)); height: calc(42 * var(--u)); padding: 0 calc(14 * var(--u)); border-radius: 99Px; background: #386aef; overflow: hidden; }
.winner-ticker > img { width: calc(18 * var(--u)); height: calc(18 * var(--u)); margin-right: calc(8 * var(--u)); border-radius: 50%; }
.ticker-window { flex: 1; height: calc(20 * var(--u)); overflow: hidden; }
.ticker-window p { height: calc(20 * var(--u)); color: #fff36a; font-size: calc(12 * var(--u)); line-height: calc(20 * var(--u)); white-space: nowrap; }
.ticker-window b, .ticker-window em { font-style: normal; font-weight: 700; }
.winner-ticker time { color: #b7cdff; font-size: calc(11 * var(--u)); }
.ticker-enter-active, .ticker-leave-active { transition: transform .32s ease, opacity .32s ease; }
.ticker-enter-from { transform: translateY(100%); opacity: 0; }
.ticker-leave-to { transform: translateY(-100%); opacity: 0; }
.ticker-avatar-enter-active, .ticker-avatar-leave-active { transition: transform .32s ease, opacity .32s ease; }
.ticker-avatar-enter-from { transform: translateY(100%); opacity: 0; }
.ticker-avatar-leave-to { transform: translateY(-100%); opacity: 0; }
.ticker-time-enter-active, .ticker-time-leave-active { transition: transform .32s ease, opacity .32s ease; }
.ticker-time-enter-from { transform: translateY(100%); opacity: 0; }
.ticker-time-leave-to { transform: translateY(-100%); opacity: 0; }

.panel-actions { position: absolute; left: calc(17 * var(--u)); top: calc(457 * var(--u)); display: flex; gap: calc(14 * var(--u)); }
.panel-actions button {
  position: relative;
  isolation: isolate;
  width: calc(154 * var(--u));
  height: calc(42 * var(--u));
  overflow: hidden;
  border: calc(1 * var(--u)) solid rgba(206, 240, 255, .72);
  border-radius: 99Px;
  color: #fff;
  background: linear-gradient(180deg, rgba(120, 198, 255, .32) 0%, rgba(65, 151, 255, .2) 52%, rgba(95, 179, 255, .25) 100%);
  box-shadow:
    inset 0 calc(1 * var(--u)) 0 rgba(255, 255, 255, .62),
    inset 0 calc(-8 * var(--u)) calc(14 * var(--u)) rgba(41, 121, 246, .2),
    0 calc(3 * var(--u)) calc(8 * var(--u)) rgba(19, 93, 218, .16);
  backdrop-filter: blur(calc(7 * var(--u))) saturate(145%);
  -webkit-backdrop-filter: blur(calc(7 * var(--u))) saturate(145%);
  font-size: calc(15 * var(--u));
  font-weight: 700;
  text-shadow: 0 calc(1 * var(--u)) calc(2 * var(--u)) rgba(26, 91, 190, .35);
}
.panel-actions button::before {
  position: absolute;
  z-index: -1;
  top: 0;
  left: 8%;
  width: 84%;
  height: 52%;
  border-radius: 0 0 50% 50%;
  background: linear-gradient(180deg, rgba(255, 255, 255, .25), rgba(255, 255, 255, .03));
  content: "";
  pointer-events: none;
}
.panel-actions button::after {
  position: absolute;
  z-index: -1;
  inset: calc(2 * var(--u));
  border: calc(1 * var(--u)) solid rgba(255, 255, 255, .1);
  border-radius: inherit;
  content: "";
  pointer-events: none;
}
.panel-actions button:active { transform: translateY(calc(1 * var(--u))); filter: brightness(.94); }

.tasks-card, .features-card { position: relative; width: calc(358 * var(--u)); margin: 0 auto calc(16 * var(--u)); overflow: hidden; border-radius: calc(20 * var(--u)); }
.tasks-card {
  height: auto;
  padding: calc(88 * var(--u)) 0 calc(8 * var(--u));
  background: #e6f8ff;
}
.section-tip { position: absolute; top: calc(58 * var(--u)); width: 100%; color: #238cdf; font-size: calc(12 * var(--u)); text-align: center; }
.tasks-card ul { padding: 0 calc(12 * var(--u)); }
.tasks-card li { position: relative; display: flex; align-items: center; height: calc(54 * var(--u)); padding-left: calc(14 * var(--u)); }
.task-marker { position: absolute; left: 0; width: calc(4 * var(--u)); height: calc(20 * var(--u)); border-radius: calc(2 * var(--u)); background: #239df5; }
.tasks-card h3 { font-size: calc(14 * var(--u)); font-weight: 500; }
.tasks-card li p { margin-top: calc(4 * var(--u)); color: #218fe3; font-size: calc(11 * var(--u)); }
.task-status { position: absolute; right: 0; display: grid; place-items: center; width: calc(68 * var(--u)); height: calc(34 * var(--u)); border: 0; border-radius: 99Px; color: #fff; background: #23a9f7; font-size: calc(12 * var(--u)); }
.task-status.done { background: #97d9ff; }

.features-card { height: auto; padding: calc(64 * var(--u)) calc(12 * var(--u)) calc(24 * var(--u)); background: #fff; }
.feature-images {
  display: flex;
  gap: calc(11 * var(--u));
  width: 100%;
  padding-bottom: calc(4 * var(--u));
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.feature-images::-webkit-scrollbar { display: none; }
.feature-images img {
  flex: 0 0 calc(104 * var(--u));
  width: calc(104 * var(--u));
  height: calc(185 * var(--u));
  border-radius: calc(8 * var(--u));
  object-fit: cover;
  scroll-snap-align: start;
  user-select: none;
  -webkit-user-drag: none;
}
.features-card > p {
  margin: calc(17 * var(--u)) calc(4 * var(--u)) 0;
  color: #64748b;
  font-size: calc(13 * var(--u));
  line-height: calc(21 * var(--u));
  white-space: normal;
  overflow-wrap: anywhere;
}

.bottom-download {
  position: fixed;
  z-index: 1000;
  bottom: calc(18 * var(--u) + env(safe-area-inset-bottom, 0px));
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(358 * var(--u));
  height: calc(52 * var(--u));
  margin: 0;
  transform: translateX(-50%);
  border: 0;
  border-radius: 99Px;
  color: #fff;
  background: #3aaaf8;
  box-shadow: 0 calc(4 * var(--u)) calc(14 * var(--u)) rgba(0, 72, 180, .3);
  font-size: calc(18 * var(--u));
  font-weight: 700;
}

:global(.game-prize-popup.van-popup) {
  overflow: visible;
  background: transparent;
}

:global(.game-prize-overlay.van-overlay) {
  background-color: rgba(0, 0, 0, 0.7);
}

:deep(.van-checkbox__icon .van-icon) {
  border: 1px solid #fff;
}

.rule-popup {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.rule-content {
  width: 588px;
  height: 624px;
  padding: 98px 0 0 25px;
  background-size: 100% 100%;
}

.rule-info {
  width: 488px;
  height: 446px;
  margin: 20px 0 0 25px;
  overflow: auto;
  font-size: 24px;
  font-weight: 800;
}

.rule-popup > button {
  width: 302px;
  height: 98px;
  border: none;
  border-radius: 198px;
  color: #fff;
  background-color: #823f3f;
  font-size: 32px;
  font-weight: 500;
}

.rule-popup > span {
  margin-top: 32px;
}

.rule-popup :deep(.van-checkbox__label) {
  color: #fff;
}

.award-content {
  width: 588px;
  height: 624px;
  padding: 98px 0 0 25px;
  background-size: 100% 100%;
}

.award-list {
  width: 500px;
  height: 485px;
  margin: 0 12px;
  padding-top: 40px;
  overflow: auto;
}

.award-list li {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 100%;
  height: 120px;
  margin-bottom: 20px;
  border-bottom: 2px solid #999;
  color: #fff;
  font-size: 24px;
  font-weight: 500;
}

.award-list li p:first-child {
  font-size: 36px;
}

.rule-info pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}

@media (prefers-reduced-motion: reduce) {
  .ticker-enter-active, .ticker-leave-active { transition: none; }
}
</style>
